// [목적] user 도메인 zod 스키마 — 공개 프로필(public) / 본인 프로필(me) / PATCH me 입력 BE/FE 공유
// [이유] folio-social-and-profile Spec WC-DTO-USER-02 — R-5 민감 필드(emailHash, marketingOptIn) 제외 강제 및
//         FE ProfilePage, PATCH /users/me API client 의 타입 단일화
// [사이드 이팩트] FE ProfilePage 탭 카운트(folioCount/followerCount/followingCount) 및 isMe 분기 UI 에 직접 영향
//                 Wave A Low-2: links 값을 z.string().url() 로 강제 — javascript: 같은 비안전 스킴 차단 (XSS 방지).
//                 Wave A Low-3: locale 을 min(2).max(10) 으로 제한 (BCP47 길이 범위).
//                 M7 P2: isPrivate(optional) 추가 — profileVisibility='private' + 비본인 조회 시 true.
//                   isPrivate=true 인 응답은 bio/coverUrl/links=null, followerCount/followingCount/folioCount=0.
// [수정 시 고려사항] handle 변경 금지 정책(Q-12) — patchUserMeInputSchema 에 handle 필드 없음.
//                   notificationPrefs JSONB 는 partial merge(서버 측 Object.assign) — 클라이언트는 변경 키만 전송
//                   profileVisibility 는 userMeSchema 전용(본인 조회) — public 스키마는 isPrivate 로만 노출.

import { z } from 'zod';

import { folioSchema } from './folio.js';

// ─── Public Profile ───────────────────────────────────────────────────────────

/**
 * GET /users/:handle 응답 data 필드.
 * emailHash·emailMasked·marketingOptIn 등 민감 필드 의도적으로 제외 (R-5).
 * isMe — viewer 가 본인일 때 true (FE 편집 버튼 노출 분기).
 * isFollowing — viewer 가 팔로우 중인지 여부 (비로그인 시 false).
 * links — Wave A Low-2: 값을 z.string().url() 로 강제 (javascript: 비안전 스킴 차단).
 * locale — Wave A Low-3: BCP47 범위 min(2).max(10) 으로 제한.
 */
export const userPublicProfileSchema = z.object({
  id: z.string().uuid(),
  handle: z.string(),
  displayName: z.string(),
  bio: z.string().nullable(),
  avatarUrl: z.string().url().nullable(),
  coverUrl: z.string().url().nullable(),
  locale: z.string().min(2).max(10), // Wave A Low-3: BCP47 길이 제한
  links: z.record(z.string().url()).nullable(), // Wave A Low-2: URL 검증으로 비안전 스킴 차단
  createdAt: z.string(),
  isMe: z.boolean(),
  isFollowing: z.boolean(),
  followerCount: z.number().int().nonnegative(),
  followingCount: z.number().int().nonnegative(),
  folioCount: z.number().int().nonnegative(),
  // M7 P2: profileVisibility='private' + 비본인 조회 시에만 true 로 포함 (제한 응답 마커)
  isPrivate: z.boolean().optional(),
});

/**
 * GET /users/me 응답 data 필드.
 * public 필드 + 본인 전용 민감/설정 필드 포함.
 * emailMasked — "t***@gmail.com" 형태로 마스킹된 이메일 (emailHash 원본 미포함).
 */
export const userMeSchema = userPublicProfileSchema.extend({
  emailMasked: z.string().nullable(),
  notificationPrefs: z.record(z.unknown()),
  marketingOptIn: z.boolean(),
  interests: z.array(z.string()),
  onboardedAt: z.string().nullable(),
  role: z.enum(['user', 'editor', 'admin']),
  // M7 P2: 프로필 공개 범위(SettingsPage 토글 실데이터) — 본인 조회 전용, public 스키마엔 미포함
  profileVisibility: z.enum(['public', 'private']),
});

// ─── PATCH me Input ───────────────────────────────────────────────────────────

/**
 * PATCH /users/me 요청 body.
 * handle 변경 불가 — 스키마에 handle 필드 없음 (Q-12 정책).
 * notificationPrefs 는 서버에서 partial merge (변경 키만 전송).
 */
export const patchUserMeInputSchema = z.object({
  displayName: z.string().min(1).max(50).optional(),
  bio: z.string().max(400).optional().nullable(),
  avatarUrl: z.string().url().max(2000).optional().nullable(),
  coverUrl: z.string().url().max(2000).optional().nullable(),
  locale: z.string().min(2).max(10).optional(),
  links: z.record(z.string().url().max(2000)).optional().nullable(),
  notificationPrefs: z.record(z.unknown()).optional(),
  marketingOptIn: z.boolean().optional(),
  interests: z.array(z.string().min(1).max(40)).max(20).optional(),
  // M7 P2: 프로필 공개 범위 토글 — SettingsPage 프로필 공개 스위치 실데이터 연동
  profileVisibility: z.enum(['public', 'private']).optional(),
});

// ─── GET /users/me/stats ──────────────────────────────────────────────────────

/**
 * GET /users/me/stats 응답 data 필드.
 * [목적] M7 P1 — HomePage 통계 카드(내 폴리오/받은 좋아요/Fork됨) 실데이터 계약.
 * [이유] folioCount/likesReceived/forkedCount 는 HomePage 표시 항목과 1:1 대응(UserStatsService).
 */
export const userStatsSchema = z.object({
  folioCount: z.number().int().nonnegative(),
  likesReceived: z.number().int().nonnegative(),
  forkedCount: z.number().int().nonnegative(),
});

// ─── GET /users/me/export ─────────────────────────────────────────────────────

/**
 * GET /users/me/export 응답 data 필드 — 내 데이터 내보내기(JSON) 전체 스냅샷.
 * [목적] FlowLine b609867a 잔여 과업 — 사용자가 본인 데이터를 단일 JSON 파일로 다운로드.
 * [이유] profile 은 userMeSchema 재사용(동일 필드), folios 는 folioSchema 재사용(GET /folios/:id
 *        상세와 같은 형태 — 단 날씨 pop·POI 라이브 보강 없이 DB 저장값만). comments/likedFolioIds/
 *        bookmarkedFolioIds/following 은 이 스키마 전용 최소 필드.
 * [사이드 이팩트] 다른 사용자의 개인정보(이메일 등)·토큰/세션/anon_session_id/token_version 은
 *                본 스키마에 필드 자체가 없어 parse 시 자동 배제(zod 기본 strip) — 스키마가 화이트리스트 역할.
 * [수정 시 고려사항] userMeSchema/folioSchema 변경 시 이 스키마는 재사용이므로 자동 반영되나,
 *                   FE 가 이 필드를 그대로 파일로 직렬화하므로 필드 제거 시 하위호환 검토 필요.
 */
export const userDataExportSchema = z.object({
  exportedAt: z.string(),
  profile: userMeSchema,
  folios: z.array(folioSchema),
  comments: z.array(
    z.object({
      id: z.string().uuid(),
      folioId: z.string().uuid(),
      text: z.string(),
      createdAt: z.string(),
    }),
  ),
  likedFolioIds: z.array(z.string().uuid()),
  bookmarkedFolioIds: z.array(z.string().uuid()),
  following: z.array(z.object({ handle: z.string() })),
});

// ─── POST /users/me/avatar-upload-url ─────────────────────────────────────────

/**
 * [목적] 업로드 대상 종류 — 아바타(프로필 사진) 또는 커버(배너) 이미지 구분
 * [이유] FlowLine b609867a 잔여 — 두 종류가 서로 다른 용량 상한(kind 별)과 저장 키 prefix 를 가진다.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] 종류 추가 시 AVATAR_UPLOAD_MAX_BYTES 매핑도 함께 갱신 필요.
 */
export const avatarUploadKindSchema = z.enum(['avatar', 'cover']);

/**
 * [목적] 업로드 허용 이미지 MIME 타입 화이트리스트 — 3종 고정
 * [이유] 임의 파일 타입(svg/실행 파일 등) 업로드로 인한 저장소 오염·XSS 방지.
 * [사이드 이팩트] BE presigned URL 발급 시 이 값을 ContentType 으로 서명 — 실 업로드 헤더가 일치해야 함.
 * [수정 시 고려사항] 타입 추가 시 apps/api user-image-upload.service.ts EXT_MAP 동시 갱신 필요.
 */
export const avatarUploadContentTypeSchema = z.enum(['image/jpeg', 'image/png', 'image/webp']);

/** kind 별 업로드 용량 상한(bytes) — avatar 2MB, cover 5MB (BE/FE 단일 소스). */
export const AVATAR_UPLOAD_MAX_BYTES: Record<z.infer<typeof avatarUploadKindSchema>, number> = {
  avatar: 2 * 1024 * 1024,
  cover: 5 * 1024 * 1024,
};

/**
 * [목적] POST /users/me/avatar-upload-url 요청 body — presigned PUT URL 발급 입력
 * [이유] FlowLine b609867a 잔여 — 프로필 사진·커버 업로드. kind 별 상한(superRefine)으로 서버가
 *        URL 을 발급하기 전에 조기 차단한다.
 * [사이드 이팩트] sizeBytes 는 클라이언트 신고값 — presigned PUT URL 자체는 실제 업로드 바이트 수를
 *                강제하지 않으므로(S3 presign 한계) 이 검증은 최종 방어선이 아니라 조기 차단용이다.
 * [수정 시 고려사항] 상한 변경 시 FE ProfileEditScreen 클라이언트 측 사전 검증 메시지도 동기 갱신.
 */
export const avatarUploadUrlInputSchema = z
  .object({
    kind: avatarUploadKindSchema,
    contentType: avatarUploadContentTypeSchema,
    sizeBytes: z.number().int().positive(),
  })
  .superRefine((val, ctx) => {
    const max = AVATAR_UPLOAD_MAX_BYTES[val.kind];
    if (val.sizeBytes > max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sizeBytes'],
        message: `${val.kind} 이미지는 최대 ${max}bytes 까지 업로드할 수 있습니다.`,
      });
    }
  });

/**
 * [목적] POST /users/me/avatar-upload-url 응답 data 필드
 * [이유] uploadUrl(단기 TTL presigned PUT) 로 브라우저가 직접 S3(호환) 버킷에 업로드하고,
 *        publicUrl 로 PATCH /users/me 를 확정 호출한다.
 * [사이드 이팩트] 없음.
 * [수정 시 고려사항] publicUrl 은 PATCH 확정 단계에서 BE 화이트리스트 검증을 통과해야 저장됨
 *                   (user-profile.service.ts — 임의 외부 URL 저장 방지).
 */
export const avatarUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  publicUrl: z.string().url(),
  expiresIn: z.number().int().positive(),
});

export type AvatarUploadKind = z.infer<typeof avatarUploadKindSchema>;
export type AvatarUploadContentType = z.infer<typeof avatarUploadContentTypeSchema>;
export type AvatarUploadUrlInput = z.infer<typeof avatarUploadUrlInputSchema>;
export type AvatarUploadUrlResponse = z.infer<typeof avatarUploadUrlResponseSchema>;

export type UserPublicProfile = z.infer<typeof userPublicProfileSchema>;
export type UserMe = z.infer<typeof userMeSchema>;
export type PatchUserMeInput = z.infer<typeof patchUserMeInputSchema>;
export type UserStats = z.infer<typeof userStatsSchema>;
export type UserDataExport = z.infer<typeof userDataExportSchema>;
